/**
 * Memory node detail panel — rounded overlay matching the workshop RightPanel design
 * (w-80, rounded-2xl border border-glass-stroke, bg-glass-fill shadow-glass-sm backdrop-blur-glass-sm).
 *
 * Three tabs mirroring the app-wide RightPanel pattern:
 *   Details  — node identity, stats, related nodes with provenance, entity metadata
 *   Logs     — mention history and source timeline
 *   Actions  — edit label/description, delete node
 */
import { useState } from "react";
import { cn } from "@/lib/cn";
import { Button, Icons } from "@/components/ui";
import type {
  MemoryNodeDetail,
  RelatedNode,
  GraphNodeType,
  EdgeSource,
} from "@/workshop/components/shapes/memory-frame/types";
import { GRAPH_NODE_COLORS } from "@/workshop/components/shapes/memory-frame/types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PanelTab = "details" | "logs" | "actions";

export interface MemoryNodePanelProps {
  node: MemoryNodeDetail | null;
  relatedNodes: RelatedNode[];
  onClose?: () => void;
  onDelete?: () => void;
  onUpdate?: (
    nodeId: string,
    updates: { label?: string; description?: string },
  ) => void;
  onNodeSelect?: (nodeId: string) => void;
  className?: string;
  "data-testid"?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getNodeIcon(type: GraphNodeType) {
  switch (type) {
    case "user_profile":
      return <Icons.Heart className="h-4 w-4 fill-current" />;
    case "person":
      return <Icons.User className="h-4 w-4" />;
    case "company":
      return <Icons.Building2 className="h-4 w-4" />;
    case "project":
      return <Icons.FolderOpen className="h-4 w-4" />;
    case "resource":
      return <Icons.Bookmark className="h-4 w-4" />;
    case "document":
      return <Icons.FileText className="h-4 w-4" />;
    case "concept":
      return <Icons.Brain className="h-4 w-4" />;
    case "process":
      return <Icons.Settings className="h-4 w-4" />;
    case "preference":
      return <Icons.SlidersHorizontal className="h-4 w-4" />;
    case "outcome":
      return <Icons.Target className="h-4 w-4" />;
    case "category":
      return <Icons.Tag className="h-4 w-4" />;
    case "chunk":
      return <Icons.Layers className="h-4 w-4" />;
    default:
      return <Icons.FileText className="h-4 w-4" />;
  }
}

function relativeTime(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}

// ---------------------------------------------------------------------------
// Tab button — matches workshop RightPanel tab style
// ---------------------------------------------------------------------------

function TabButton({
  label,
  isActive,
  onClick,
  testId,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "flex-1 rounded-lg py-1.5 text-center text-[11px] font-medium transition-colors",
        isActive
          ? "bg-glass-fill-heavy text-primary"
          : "text-muted hover:bg-glass-stroke-subtle hover:text-secondary",
      )}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Section label
// ---------------------------------------------------------------------------

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[11px] font-semibold uppercase tracking-wide text-secondary">
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Source evidence card (expandable)
// ---------------------------------------------------------------------------

function SourceCard({ source }: { source: EdgeSource }) {
  return (
    <div className="space-y-1 rounded-lg border border-glass-stroke-subtle bg-glass-fill-heavy p-2 text-[9px]">
      <div className="flex items-center justify-between">
        <span className="font-medium capitalize text-accent">
          {source.type.replace(/_/g, " ")}
        </span>
        <span className="text-muted">
          {(source.confidence * 100).toFixed(0)}%
        </span>
      </div>
      {source.metadata?.llm_reasoning && (
        <div className="rounded bg-accent/10 p-1 text-[8px] text-accent/80">
          <span className="font-medium">Why: </span>
          {source.metadata.llm_reasoning}
        </div>
      )}
      {source.metadata?.message_snippet && (
        <div className="border-l-2 border-glass-stroke pl-1.5 text-[8px] italic text-muted">
          &ldquo;{source.metadata.message_snippet}&rdquo;
        </div>
      )}
      {source.metadata?.entity_extraction_confidence !== undefined && (
        <div className="text-[8px] text-muted">
          Extraction:{" "}
          {(source.metadata.entity_extraction_confidence * 100).toFixed(0)}%
        </div>
      )}
      {(source.metadata?.similar_mentions ?? 0) > 0 && (
        <div className="text-[8px] text-muted">
          Mentioned {source.metadata?.similar_mentions}x in source
        </div>
      )}
      <div className="text-[8px] text-muted">
        {source.timestamp ? relativeTime(source.timestamp) : "Recently"}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Related node row with provenance
// ---------------------------------------------------------------------------

function RelatedNodeRow({
  related,
  nodeKey,
  isSourcesExpanded,
  onToggleSources,
  onNodeSelect,
}: {
  related: RelatedNode;
  nodeKey: string;
  isSourcesExpanded: boolean;
  onToggleSources: () => void;
  onNodeSelect?: (id: string) => void;
}) {
  const relatedColor =
    related.color || GRAPH_NODE_COLORS[related.type] || "#6B7280";
  return (
    <div className="space-y-1">
      <button
        data-testid={`memory-node-panel-related-${related.id}`}
        onClick={() => onNodeSelect?.(related.id)}
        className="w-full flex items-center gap-2 rounded-xl bg-glass-fill-heavy p-2 text-left transition-colors hover:bg-glass-fill-dense"
      >
        <div
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${relatedColor}20`, color: relatedColor }}
        >
          {getNodeIcon(related.type)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="truncate text-[11px] font-medium text-primary">
            {related.name}
          </p>
          {related.claimText ? (
            <p className="truncate text-[10px] italic text-muted">
              &ldquo;{related.claimText}&rdquo;
            </p>
          ) : (
            <p className="text-[10px] text-muted">
              {related.direction === "outgoing" ? "→" : "←"}{" "}
              {related.relationship}
            </p>
          )}
          {related.confidence !== undefined && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1">
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[9px]",
                  related.confidence >= 0.8
                    ? "bg-success-muted text-success"
                    : related.confidence >= 0.5
                      ? "bg-warning-muted text-warning"
                      : "bg-danger-muted text-danger",
                )}
              >
                {(related.confidence * 100).toFixed(0)}% conf
              </span>
              {(related.usageCount ?? 0) > 0 && (
                <span className="rounded-full bg-success-muted px-1.5 py-0.5 text-[9px] text-success">
                  ✓ {related.usageCount}x
                </span>
              )}
              {(related.correctionCount ?? 0) > 0 && (
                <span className="rounded-full bg-danger-muted px-1.5 py-0.5 text-[9px] text-danger">
                  ✗ {related.correctionCount}x
                </span>
              )}
              {related.sources && related.sources.length > 0 && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSources();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onToggleSources();
                    }
                  }}
                  className="flex cursor-pointer items-center gap-0.5 rounded-full bg-accent/10 px-1.5 py-0.5 text-[9px] text-accent hover:bg-accent/20 transition-colors"
                >
                  <Icons.ChevronDown
                    className={cn(
                      "h-2.5 w-2.5 transition-transform",
                      isSourcesExpanded && "rotate-180",
                    )}
                  />
                  {related.sources.length} src
                </div>
              )}
            </div>
          )}
        </div>
      </button>

      {related.sources && related.sources.length > 0 && isSourcesExpanded && (
        <div className="ml-2 space-y-1 border-l-2 border-glass-stroke pl-2">
          {related.sources.map((source, si) => (
            <SourceCard key={`${nodeKey}-src-${si}`} source={source} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Details
// ---------------------------------------------------------------------------

function DetailsTab({
  node,
  relatedNodes,
  expandedSources,
  onToggleSources,
  onNodeSelect,
}: {
  node: MemoryNodeDetail;
  relatedNodes: RelatedNode[];
  expandedSources: Set<string>;
  onToggleSources: (key: string) => void;
  onNodeSelect?: (id: string) => void;
}) {
  const nodeColor = node.color || GRAPH_NODE_COLORS[node.type] || "#6B7280";

  // Flatten node metadata
  const flatMetaEntries: Array<[string, unknown]> = [];
  if (node.metadata) {
    const skip = new Set(["description"]);
    Object.entries(node.metadata).forEach(([key, value]) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return;
      if (value && typeof value === "object" && !Array.isArray(value)) {
        Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
          if (!skip.has(k) && v && !(Array.isArray(v) && v.length === 0))
            flatMetaEntries.push([k, v]);
        });
      } else {
        if (!skip.has(key)) flatMetaEntries.push([key, value]);
      }
    });
  }

  return (
    <div className="flex flex-col" data-testid="memory-node-panel-details">
      {/* Node identity */}
      <div className="flex flex-col gap-2 border-b border-glass-stroke-subtle px-3.5 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className="inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize"
            style={{
              color: nodeColor,
              borderColor: `${nodeColor}40`,
              background: `${nodeColor}18`,
            }}
          >
            {node.type}
          </span>
          {node.source_type && (
            <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] text-accent">
              {node.source_type === "auto_discovered"
                ? "Auto-discovered"
                : "Manual"}
            </span>
          )}
        </div>
        {node.mention_count !== undefined && (
          <div className="flex items-center gap-1 text-[11px] text-muted">
            <Icons.MessageCircle className="h-3 w-3 shrink-0" />
            <span>Mentioned {node.mention_count}x</span>
            {node.updated_at && <span> · {relativeTime(node.updated_at)}</span>}
          </div>
        )}
        {node.description && (
          <p className="text-[11px] leading-relaxed text-secondary">
            {node.description}
          </p>
        )}
      </div>

      {/* Related nodes */}
      {relatedNodes.length > 0 && (
        <div className="flex flex-col border-b border-glass-stroke-subtle">
          <div className="flex items-center gap-1.5 px-3.5 pt-3 pb-2">
            <SectionLabel>Related</SectionLabel>
            <span className="text-[10px] text-muted">
              ({relatedNodes.length})
            </span>
          </div>
          <div className="flex flex-col gap-1.5 px-3 pb-3">
            {relatedNodes.map((related, index) => {
              const nodeKey = `${related.id}-${related.relationship}-${related.direction}-${index}`;
              return (
                <RelatedNodeRow
                  key={nodeKey}
                  related={related}
                  nodeKey={nodeKey}
                  isSourcesExpanded={expandedSources.has(nodeKey)}
                  onToggleSources={() => onToggleSources(nodeKey)}
                  onNodeSelect={onNodeSelect}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Entity details */}
      {flatMetaEntries.length > 0 && (
        <div className="px-3.5 pt-3 pb-3">
          <div className="mb-2">
            <SectionLabel>Entity Details</SectionLabel>
          </div>
          <div className="flex flex-col gap-2">
            {flatMetaEntries.map(([key, value]) => (
              <div
                key={key}
                className="flex items-baseline justify-between gap-2"
              >
                <span className="shrink-0 text-[11px] text-muted">
                  {key
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                </span>
                <span className="truncate text-right text-[11px] font-medium text-secondary">
                  {Array.isArray(value)
                    ? (value as unknown[]).join(", ")
                    : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Logs
// ---------------------------------------------------------------------------

const MOCK_LOG_ENTRIES = [
  {
    time: "14:32",
    text: 'Mentioned in conversation — "Ask Alice about the roadmap"',
    dotColor: "var(--color-accent)",
  },
  {
    time: "14:28",
    text: 'Relationship "works at Berlin Office" auto-discovered',
    dotColor: "var(--color-accent)",
  },
  {
    time: "Yesterday",
    text: "Node created from meeting notes extraction",
    dotColor: "var(--color-muted)",
  },
  {
    time: "3 days ago",
    text: "Description updated manually",
    dotColor: "var(--color-muted)",
  },
  {
    time: "5 days ago",
    text: "Relationship confidence adjusted by user correction",
    dotColor: "var(--color-warning)",
  },
];

function LogsTab() {
  return (
    <div
      className="flex flex-col gap-1 p-3"
      data-testid="memory-node-panel-logs"
    >
      {MOCK_LOG_ENTRIES.map((entry, i) => (
        <div key={i} className="flex gap-2.5 py-1.5">
          <div className="flex flex-col items-center">
            <div
              className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full"
              style={{ backgroundColor: entry.dotColor }}
            />
            {i < MOCK_LOG_ENTRIES.length - 1 && (
              <div className="mt-1 w-px flex-1 bg-glass-stroke-subtle" />
            )}
          </div>
          <div className="min-w-0 pb-2">
            <p className="text-[11px] leading-relaxed text-secondary">
              {entry.text}
            </p>
            <p className="mt-0.5 text-[10px] text-muted">{entry.time}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Actions
// ---------------------------------------------------------------------------

function ActionsTab({
  node,
  onDelete,
  onUpdate,
}: {
  node: MemoryNodeDetail;
  onDelete?: () => void;
  onUpdate?: (
    nodeId: string,
    updates: { label?: string; description?: string },
  ) => void;
}) {
  const [editLabel, setEditLabel] = useState(node.label || node.name);
  const [editDescription, setEditDescription] = useState(
    node.description || "",
  );
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    onUpdate?.(node.id, {
      label: editLabel !== node.label ? editLabel : undefined,
      description: editDescription || undefined,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div
      className="flex flex-col gap-3 p-3.5"
      data-testid="memory-node-panel-actions"
    >
      {/* Edit form */}
      <div className="flex flex-col gap-2">
        <SectionLabel>Edit Node</SectionLabel>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] text-muted">Label</label>
          <input
            data-testid="memory-node-panel-edit-label"
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            className="w-full rounded-lg border border-glass-stroke bg-glass-fill px-2.5 py-1.5 text-[11px] text-primary placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] text-muted">Description</label>
          <textarea
            data-testid="memory-node-panel-edit-description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            rows={3}
            placeholder="Add a description..."
            className="w-full resize-none rounded-lg border border-glass-stroke bg-glass-fill px-2.5 py-1.5 text-[11px] text-primary placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <Button
          data-testid="memory-node-panel-save"
          size="sm"
          className="h-7 w-full rounded-lg text-[11px]"
          onClick={handleSave}
        >
          {saved ? (
            <>
              <Icons.CheckCircle className="mr-1.5 h-3 w-3" />
              Saved
            </>
          ) : (
            <>
              <Icons.Edit className="mr-1.5 h-3 w-3" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Danger zone */}
      <div className="border-t border-glass-stroke-subtle pt-3">
        <SectionLabel>Danger Zone</SectionLabel>
        <p className="mt-1.5 mb-2.5 text-[11px] leading-relaxed text-muted">
          Deleting this node will also remove all its connections permanently.
        </p>
        <Button
          data-testid="memory-node-panel-delete"
          variant="ghost"
          size="sm"
          className="h-7 w-full rounded-lg text-[11px] text-danger hover:bg-danger-muted"
          onClick={onDelete}
        >
          <Icons.Trash2 className="mr-1.5 h-3 w-3" />
          Delete Node
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel root
// ---------------------------------------------------------------------------

export function MemoryNodePanel({
  node,
  relatedNodes,
  onClose,
  onDelete,
  onUpdate,
  onNodeSelect,
  className,
  "data-testid": testId = "memory-node-panel",
}: MemoryNodePanelProps) {
  const [activeTab, setActiveTab] = useState<PanelTab>("details");
  const [expandedSources, setExpandedSources] = useState<Set<string>>(
    new Set(),
  );

  if (!node) return null;

  const nodeColor = node.color || GRAPH_NODE_COLORS[node.type] || "#6B7280";

  const toggleSources = (key: string) => {
    setExpandedSources((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <aside
      data-testid={testId}
      className={cn(
        "flex w-80 flex-col rounded-2xl border border-glass-stroke bg-glass-fill shadow-glass-sm backdrop-blur-glass-sm",
        className,
      )}
    >
      {/* Header */}
      <div className="flex h-11 items-center gap-2 border-b border-glass-stroke-subtle px-3.5">
        <div
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md"
          style={{ backgroundColor: `${nodeColor}20`, color: nodeColor }}
        >
          {getNodeIcon(node.type)}
        </div>
        <span className="flex-1 truncate text-xs font-semibold text-primary">
          {node.label || node.name}
        </span>
        <button
          data-testid="memory-node-panel-close"
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          className="flex items-center justify-center rounded-md p-0.5 text-muted transition-colors hover:text-primary"
        >
          <Icons.X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Tab bar — matches workshop RightPanel */}
      <div
        className="flex gap-1 border-b border-glass-stroke-subtle px-2 py-1"
        role="tablist"
        aria-label="Node panel tabs"
      >
        <TabButton
          label="Details"
          isActive={activeTab === "details"}
          onClick={() => setActiveTab("details")}
          testId="memory-node-panel-tab-details"
        />
        <TabButton
          label="Logs"
          isActive={activeTab === "logs"}
          onClick={() => setActiveTab("logs")}
          testId="memory-node-panel-tab-logs"
        />
        <TabButton
          label="Actions"
          isActive={activeTab === "actions"}
          onClick={() => setActiveTab("actions")}
          testId="memory-node-panel-tab-actions"
        />
      </div>

      {/* Scrollable content */}
      <div className="scrollbar-none flex-1 overflow-y-auto">
        {activeTab === "details" && (
          <DetailsTab
            node={node}
            relatedNodes={relatedNodes}
            expandedSources={expandedSources}
            onToggleSources={toggleSources}
            onNodeSelect={onNodeSelect}
          />
        )}
        {activeTab === "logs" && <LogsTab />}
        {activeTab === "actions" && (
          <ActionsTab node={node} onDelete={onDelete} onUpdate={onUpdate} />
        )}
      </div>
    </aside>
  );
}
